const { PAGE_NAME } = require("@wattpad/client-platform-web-events");

const READER_REGEX = /^\/(\d+)(?:-[^\/\?]+)?(?:\/page\/(\d+))?(?:\?.*)?$/;
const BROWSE_REGEX = /^\/stories\/([^/]+)(?:\?.*)?$/;
const BROWSE_LANDING_REGEX = /^\/search(?:\?.*)?$/;
const SEARCH_REGEX = /^\/search\/([^/]+)(?:\/people|\/stories)?(?:\?.*)?$/;
const PROFILE_REGEX = /^\/user\/([^/]+)(?:\?.*)?$/;

const getCurrentPathname = utils => {
  return utils?.extractUriFromUrl(window.location.href) || "";
};

const extractPartIdFromReaderPathname = pathname => {
  const match = pathname.match(READER_REGEX);
  return match ? Number(match[1]) : null;
};

const extractTopicFromBrowsePathname = pathname => {
  const match = pathname.match(BROWSE_REGEX);
  return match ? match[1] : null;
};

/**
 * Extracts page details based on the current pathname.
 *
 * @param {object} utils - Utility functions to extract the URI from the current URL.
 * @returns {object|null} - An object containing pageName and any resource IDs if matched.
 */
const getPageDetails = utils => {
  const pathname = getCurrentPathname(utils);

  if (!pathname) {
    console.error("usePage: No pathname found");
    return null;
  }

  if (READER_REGEX.test(pathname)) {
    return {
      pageName: PAGE_NAME.reader,
      partId: extractPartIdFromReaderPathname(pathname)
    };
  } else if (BROWSE_REGEX.test(pathname)) {
    const topic = extractTopicFromBrowsePathname(pathname);
    return {
      pageName: PAGE_NAME.browse,
      topic
    };
  } else if (BROWSE_LANDING_REGEX.test(pathname)) {
    return {
      pageName: PAGE_NAME.browse_landing
    };
  } else if (SEARCH_REGEX.test(pathname)) {
    return {
      pageName: PAGE_NAME.search
    };
  } else if (PROFILE_REGEX.test(pathname)) {
    return {
      pageName: PAGE_NAME.profile
    };
  }

  return null;
};

/**
 * Builds an event object for the prompt view event based on the component name,
 * and current page details via the getPageDetails function.
 *
 * @param {string} componentName - The name of the component triggering the event.
 * @param {object} utils - Utility functions to extract page details.
 * @param {object} extraPageDetails - Additional details to include in the event page details.
 * @returns {object|undefined} - The event object or undefined if no page details are found.
 */
const buildPromptViewEvent = (componentName, utils) => {
  const pageDetails = getPageDetails(utils);
  if (!pageDetails) {
    return undefined;
  }
  const { pageName, partId, topic } = pageDetails;
  let eventDetails = {
    page: {
      [pageName]: {}
    },
    component: componentName
  };

  switch (pageName) {
    case PAGE_NAME.reader:
      eventDetails.page[pageName].partid = partId;
      break;
    case PAGE_NAME.browse:
      eventDetails.page[pageName].topic = topic;
      break;
  }

  return eventDetails;
};

/**
 * Extracts the storyId from a view instance if available.
 * Supports CoreStoryReading and Authentication views.
 *
 * @param {object} app - The application instance containing views.
 * @param {object} toView - The view instance to extract the storyId from.
 * @returns {number|null} The extracted storyId, or null if not found.
 */
function extractStoryIdFromView(app, toView) {
  // Main reading page
  if (toView instanceof app.views?.CoreStoryReading) {
    return Number(toView.model.get("group")?.id);
  }
  // Mature gated full page auth view on reading page
  if (toView instanceof app.views?.Authentication && !!toView.storyId) {
    return Number(toView.storyId);
  }
  return null;
}

function extractPartIdFromView(app, toView) {
  if (toView instanceof app.views?.CoreStoryReading) {
    return Number(toView.model.get("id"));
  }
  // Mature gated full page auth view on reading page
  if (toView instanceof app.views?.Authentication && !!toView.partId) {
    return Number(toView.partId);
  }
  return null;
}

module.exports = {
  getPageDetails,
  buildPromptViewEvent,
  extractStoryIdFromView,
  extractPartIdFromView,
  READER_REGEX,
  BROWSE_REGEX,
  BROWSE_LANDING_REGEX,
  SEARCH_REGEX,
  PROFILE_REGEX
};
